import React, { useState, useRef } from 'react';
import api from '../../services/api';

/**
 * PODocumentParser
 * Props:
 *   onApply(fields): called with extracted fields when user clicks "Apply to Form"
 *   disabled: boolean
 */
export default function PODocumentParser({ onApply, disabled }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const parseFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('document', file);
      const { data } = await api.post('/ocr/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Parsing failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  const handleApply = () => {
    if (result?.extracted) {
      onApply(result.extracted);
      setOpen(false);
      setResult(null);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 7,
          border: '1px dashed #0891b2', background: '#f0f9ff',
          color: '#0891b2', cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.15s',
        }}
        onMouseEnter={e => !disabled && (e.currentTarget.style.background = '#e0f2fe')}
        onMouseLeave={e => (e.currentTarget.style.background = '#f0f9ff')}
      >
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="12" y1="18" x2="12" y2="12"/>
          <line x1="9" y1="15" x2="15" y2="15"/>
        </svg>
        Parse PO Document
      </button>
    );
  }

  const ext = result?.extracted || {};
  const hasFields = Object.keys(ext).length > 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={() => { setOpen(false); setResult(null); }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24,
        width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Parse PO Document</h3>
          <button onClick={() => { setOpen(false); setResult(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.2rem', lineHeight: 1 }}>×</button>
        </div>

        {!result && !loading && (
          <>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? '#0891b2' : '#cbd5e1'}`,
                borderRadius: 10, padding: '32px 16px',
                textAlign: 'center', cursor: 'pointer',
                background: dragOver ? '#f0f9ff' : '#f8fafc',
                transition: 'all 0.15s',
              }}
            >
              <svg width="32" height="32" fill="none" stroke={dragOver ? '#0891b2' : '#94a3b8'} strokeWidth="1.5" viewBox="0 0 24 24" style={{ margin: '0 auto 8px', display: 'block' }}>
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <div style={{ fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>Drop PDF or TXT file here</div>
              <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: 4 }}>or click to browse · max 10MB</div>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.txt" style={{ display: 'none' }} onChange={e => parseFile(e.target.files?.[0])} />
          </>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <div style={{ width: 36, height: 36, border: '3px solid #dbeafe', borderTopColor: '#1d4ed8', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            <div style={{ color: '#64748b' }}>Extracting text…</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        )}

        {error && (
          <div style={{ padding: '12px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: '0.82rem', marginTop: 12 }}>
            {error}
          </div>
        )}

        {result && !loading && (
          <div>
            <div style={{ padding: '10px 12px', background: hasFields ? '#d1fae5' : '#fef3c7', border: `1px solid ${hasFields ? '#6ee7b7' : '#fcd34d'}`, borderRadius: 8, fontSize: '0.82rem', color: hasFields ? '#065f46' : '#92400e', marginBottom: 16 }}>
              {result.message}
            </div>

            {hasFields && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {ext.po_number  && <Field label="PO Number"   value={ext.po_number} />}
                {ext.po_date    && <Field label="Date"        value={ext.po_date} />}
                {ext.buyer_name && <Field label="Buyer"       value={ext.buyer_name} />}
                {ext.seller_name && <Field label="Vendor"     value={ext.seller_name} />}
                {ext.gstin      && <Field label="GSTIN"       value={ext.gstin} />}
                {ext.total      && <Field label="Total"       value={`₹${ext.total.toLocaleString('en-IN')}`} />}
                {ext.items?.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Line Items ({ext.items.length})
                    </div>
                    {ext.items.slice(0, 5).map((item, i) => (
                      <div key={i} style={{ padding: '6px 10px', background: '#f8fafc', borderRadius: 6, fontSize: '0.78rem', color: '#334155', marginBottom: 4 }}>
                        <strong>{item.name}</strong> — Qty: {item.quantity}{item.unit ? ` ${item.unit}` : ''} @ ₹{item.unit_price}
                      </div>
                    ))}
                    {ext.items.length > 5 && <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>+{ext.items.length - 5} more items</div>}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {hasFields && (
                <button className="btn btn-primary" onClick={handleApply} style={{ flex: 1, fontSize: '0.85rem' }}>
                  Apply to Form
                </button>
              )}
              <button onClick={() => { setResult(null); setError(''); }} style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#475569' }}>
                {hasFields ? 'Try Another' : 'Try Another File'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 80, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.85rem', color: '#0f172a', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
